/* Achievement-unlock celebration. Any component can fire it:
 *   const celebrate = useCelebrate(); celebrate(['rate_first'])
 * Shows a one-at-a-time modal with the interactive 3D badge and a short confetti
 * burst. Dependency-free (canvas confetti, no npm add); reduced-motion shows the
 * modal without confetti. Unknown keys are ignored, so it's safe to pass the raw
 * `unlocked` array straight from a vote/claim response. */
import { createContext, useContext, useState, useCallback, useRef, useEffect, lazy, Suspense, type ReactNode } from 'react'
import { Modal, Button } from '@heroui/react'
import { badgeArt } from './badgeArt'

import { ErrorBoundary } from './ErrorBoundary'
const BadgeViewer = lazy(() => import('./BadgeViewer'))

// Mirrors achievements.py DEFS (asset == key for every current badge).
const ACHV_META: Record<string, { title: string; description: string; asset: string }> = {
  rate_first: { title: 'First Verdict', description: 'You rated your first server.', asset: 'rate_first' },
  rate_three: { title: 'Field Surveyor', description: 'You rated three servers.', asset: 'rate_three' },
  invite_5: { title: 'Squad Leader', description: 'Five recruits joined from your invite.', asset: 'invite_5' },
  invite_10: { title: 'Clan Recruiter', description: 'Ten recruits joined from your invite.', asset: 'invite_10' },
}

type CelebrateFn = (keys: string[]) => void
const Ctx = createContext<CelebrateFn>(() => { })
export const useCelebrate = () => useContext(Ctx)

export function CelebrationProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<string[]>([])
  const current = queue[0] ?? null
  const celebrate = useCallback((keys: string[]) => {
    const valid = (keys || []).filter((k) => ACHV_META[k])
    if (valid.length) setQueue((q) => [...q, ...valid.filter((k) => !q.includes(k))])
  }, [])
  const close = useCallback(() => setQueue((q) => q.slice(1)), [])
  return (
    <Ctx.Provider value={celebrate}>
      {children}
      {current && <CelebrationModal key={current} achKey={current} onClose={close} />}
    </Ctx.Provider>
  )
}

function Confetti() {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    const canvas = ref.current
    if (reduce || !canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    const W = window.innerWidth, H = window.innerHeight
    canvas.width = W * dpr; canvas.height = H * dpr
    ctx.scale(dpr, dpr)
    const colors = ['#FF3C15', '#ffd34d', '#7fd1ff', '#9be08a', '#ffffff']
    const parts = Array.from({ length: 130 }, () => ({
      x: W / 2, y: H * 0.4,
      vx: (Math.random() * 2 - 1) * 7,
      vy: (Math.random() * -1 - 0.15) * 9,
      g: 0.22 + Math.random() * 0.12,
      s: 4 + Math.random() * 5,
      rot: Math.random() * Math.PI, vr: (Math.random() * 2 - 1) * 0.3,
      c: colors[(Math.random() * colors.length) | 0],
      life: 0,
    }))
    let raf = 0, t0 = performance.now()
    const tick = (t: number) => {
      const dt = Math.min((t - t0) / 16.67, 2); t0 = t
      ctx.clearRect(0, 0, W, H)
      let alive = false
      for (const p of parts) {
        p.life += dt
        p.vy += p.g * dt; p.x += p.vx * dt; p.y += p.vy * dt; p.rot += p.vr * dt
        const a = Math.max(0, 1 - p.life / 95)
        if (a > 0 && p.y < H + 20) {
          alive = true
          ctx.save(); ctx.globalAlpha = a; ctx.translate(p.x, p.y); ctx.rotate(p.rot)
          ctx.fillStyle = p.c; ctx.fillRect(-p.s / 2, -p.s / 2, p.s, p.s * 0.6); ctx.restore()
        }
      }
      if (alive) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])
  return (
    <canvas ref={ref} aria-hidden
      style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: 9999 }} />
  )
}

function CelebrationModal({ achKey, onClose }: { achKey: string; onClose: () => void }) {
  const meta = ACHV_META[achKey]
  const art = badgeArt(meta.asset)
  return (
    <>
      <Confetti />
      <Modal.Backdrop isOpen onOpenChange={(o) => !o && onClose()}>
        <Modal.Container>
          <Modal.Dialog className="max-w-[380px] w-full">
            <Modal.Body className="text-center py-8 px-6">
              <div className="mono text-[11px] uppercase tracking-wider text-[var(--brand)] mb-3">Achievement unlocked</div>
              <div className="mx-auto mb-4 w-full" style={{ maxWidth: 200 }}>
                <ErrorBoundary fallback={<div style={{ width: '100%', maxWidth: 200, aspectRatio: '1' }} className="mx-auto rounded-full bg-[var(--background-tertiary)]" />}>
                  <Suspense fallback={<div style={{ width: '100%', maxWidth: 200, aspectRatio: '1' }} className="mx-auto rounded-full bg-[var(--background-tertiary)] animate-pulse" />}>
                    <BadgeViewer color={art.color} normal={art.normal} size={200} locked={false} />
                  </Suspense>
                </ErrorBoundary>
              </div>
              <h2 className="text-xl font-semibold">{meta.title}</h2>
              <p className="text-[var(--muted)] text-sm mt-1.5">{meta.description}</p>
            </Modal.Body>
            <Modal.Footer><Button slot="close" className="w-full">Nice</Button></Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </>
  )
}
